/**
 * v4 角色体系 — 6 角色 + policy_editor 占位（Q7 决策 a）。
 *
 * 与后端 app/core/auth.py Role 枚举字符串保持一致。
 */

export const ROLES = [
  'platform_operator', // 平台运营方（跨租户代服）
  'tenant_admin',      // 租户管理员（孵化器自营）
  'manager',           // 管理者（租户内）
  'staff',             // 企服
  'enterprise',        // 企业主
  'mentor',            // 导师
  'investor',          // 投资方
  'policy_editor',     // 政策包编辑（Q7 占位；M3-β 后真启）
  'auditor',           // 审计员（M3-ε 后真启）
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  platform_operator: '平台运营方',
  tenant_admin:      '租户管理员',
  manager:           '管理者',
  staff:             '企服',
  enterprise:        '企业',
  mentor:            '导师',
  investor:          '投资方',
  policy_editor:     '政策编辑',
  auditor:           '审计员',
};

export const ROLE_COLORS: Record<Role, string> = {
  platform_operator: 'purple',
  tenant_admin:      'geekblue',
  manager:           'blue',
  staff:             'green',
  enterprise:        'gold',
  mentor:            'cyan',
  investor:          'orange',
  policy_editor:     'magenta',
  auditor:           'volcano',
};

/** 菜单可见性矩阵 — 用于 Sidebar 过滤。 */
export interface MenuPermissionMatrix {
  /** key → 允许的角色列表（命中即可见） */
  [menuKey: string]: Role[];
}

// ⚠️ MANAGER 不一致约定（ρ-3 backlog L52 / ρ-4 R-low#3 治理决策，保留至 R2）：
//   后端 backend_app/app/core/auth.py:21 `class Role` 当前只有
//     platform_operator / tenant_admin / staff / policy_editor / enterprise
//   —— **没有 MANAGER**。前端 `manager` 是 v3 遗留枚举（参考 bak/backend），
//   登录该角色后访问任何后端业务接口都会 403。
//
//   决策：暂保持现状（不从已对齐的菜单删除 `manager`），原因：
//     1) /cases 与 /monitoring 已与后端 ROLE_PERMISSIONS 严格对齐（不含 manager），
//        这两条路径不会出现 403 死页。
//     2) 其余 /dashboard /subjects /ledger /compliance /readiness /materials /cockpit
//        在 R2 与后端 Role.MANAGER（或权限点）入库时一并对齐 —— 删早了会让
//        老 manager 账号瞬间失去 UX 入口；保留至少能让用户看到"原本可见"的菜单。
//     3) 真守卫在后端 require_permission；前端越权也只是 UX 装饰。
//   R2 待办：要么后端补 Role.MANAGER + ROLE_PERMISSIONS；要么前端 lib/role.ts
//   去除 manager 枚举本身（并清理 6 个菜单 + 颜色/标签映射）。
// R3-Sprint13-fix + Sprint14:严格分工 — platform_operator 不看租户业务菜单(主体/申报/台账/...)。
// 设计原因:SaaS 平台运营方应聚焦平台级(租户/用户/政策包/审计/LLM/白标),
// 租户业务由 tenant_admin/staff 操作。
//
// [Sprint14] 代登(impersonate)已下线 — SaaS 不可推卸操作记录原则:谁的账号谁负责。
// 如需协助租户,通过审计日志只读定位问题,或联系该租户管理员自行操作。
// /dashboard 对 platform_operator 走"跨租户聚合"视图(/_admin/platform-stats)。
export const MENU_VISIBILITY: MenuPermissionMatrix = {
  '/dashboard':         ['platform_operator', 'tenant_admin', 'manager', 'staff'],
  // Sprint 32-C: 我的待办 — 系统主动推 "今天该追什么";staff 默认落地页
  // tenant_admin 也开放(scope=team 看团队聚合);platform_operator 不需要(无 assignment)
  '/my-todos':          ['tenant_admin', 'manager', 'staff'],
  '/subjects':          ['tenant_admin', 'manager', 'staff'],
  // M3-ρ-3 SR-med#1: /cases 显式与后端 ROLE_PERMISSIONS 对齐（不含 manager）
  '/cases':             ['tenant_admin', 'staff'],
  // Sprint 20-extra:企业资质申报看板(管理者视角 — 走 listCases,与 /cases 同权限基线)
  // 严格分工修(2026-07-01):申报是租户日常业务,platform_operator 不看(跨租户走聚合仪表盘)。
  '/declaration-dashboard': ['tenant_admin', 'manager', 'staff'],
  // S2 退 R1(2026-07-01):'/ledger' 菜单键已删(企业台账页 + /periods/{id}/ledger 端点退役)。
  // Sprint 52 Phase 1C:应收账单看板 —— 应收账单是**每个租户私有**(租户自己的账单),非平台计费。
  // 严格分工修(2026-07-01):platform_operator 不看租户私有账单(跨租户走聚合仪表盘 + 审计);移除。
  '/receivables':       ['tenant_admin', 'staff'],
  '/compliance':        ['tenant_admin', 'manager', 'staff'],
  // Sprint 20-extra: /readiness 已并入 /compliance(孵化器达标看板),菜单项与 RoleGuard 一并下线;
  // /readiness 路由仅做 <Navigate to="/compliance">,不再需要权限矩阵条目。
  '/materials':         ['tenant_admin', 'manager', 'staff'],
  // Sprint41:/cockpit 已废 — 真业务由「我的待办」覆盖,菜单 + 矩阵条目删
  // M3-ρ-5：监控/对比看板 — 与后端 monitoring:read / monitoring:snapshot 对齐（不含 manager）
  '/monitoring':        ['tenant_admin', 'staff'],
  // R3-Sprint12-A-UX10 + Sprint14 注释订正:政策包是**平台公共目录**(policy_packs 在
  // PLATFORM_TABLES 白名单内,无 RLS,全平台共享一份)— 不是租户私有内容。
  // 设计语义:政策本身是公开文件(国家/省/市发布的政府文件);平台/policy_editor 编纂
  // 指标库,各租户从目录里挑选自己适用的政策来申报。真正租户私有的是 declaration_case
  // (申报案)+ material_item(申报材料)+ enterprise — 那些有 RLS。
  // 4 角色都进同一页,内部用 canEdit (platform_operator/policy_editor) 二级守卫区分:
  //   - platform_operator/policy_editor: 发布/审核者视角(AI 起草 + 编辑 + 激活)
  //   - tenant_admin/staff:              目录使用者视角(只读浏览 + 挑选)
  // 后端 policy:read 见 auth.py:212/243。
  '/policy-packs':      ['platform_operator', 'policy_editor', 'tenant_admin', 'staff'],
  '/audit-logs':        ['platform_operator', 'tenant_admin', 'auditor'],
  '/tenants/theme':     ['platform_operator', 'tenant_admin'],
  // R3-Sprint10:用户/租户生命周期 UI(v5 §13)
  '/_admin/tenants':    ['platform_operator'],
  // R3-Sprint11 R-MED-5:staff 也可进入 /_admin/users(仅能用"邀请用户"功能 — invite:create);
  // 列表/管理已发邀请由 UsersAdminPage 自身按 role 隐藏(staff 无 user:read / invite:read)。
  '/_admin/users':      ['platform_operator', 'tenant_admin', 'staff'],
  // R3-Sprint13 Phase 2:LLM 提供方配置 + 调用历史 — llm:admin 仅 PLATFORM 操作员
  // (后端 require_permission("llm:admin") 兜底;前端 menu 仅 platform_operator 见入口)
  '/_admin/llm-settings': ['platform_operator'],
  // Sprint 16:资质目录 — 平台公共目录,仅 platform_operator 可编辑
  '/_admin/qualifications': ['platform_operator'],
  // Sprint 20 S20-B:孵化器档案录入 — T1-T4 数据源;tenant_admin 操作者。
  // 严格分工修(2026-07-01):代登已于 Sprint14 下线 → platform_operator 不代配置,移除。
  '/incubator/profile':   ['tenant_admin'],
  // Sprint57-C / Phase C:报表中心(政府数据交付中枢)— 租户业务。
  // 严格分工修(2026-07-01):platform_operator 不代租户运营出报表,移除(BE 仍守 enterprise:read_all)。
  '/reports':             ['tenant_admin', 'manager', 'staff'],
  // Sprint57-D / Phase D:任务系统 + JXY 驾驶舱
  //   /tasks/my-todos — WY 今日任务(企服一线 + 管理者也可看自己的)
  //   /tasks          — JXY 任务中心(派工 + 全租户列表;staff 一般不需要)
  //   /dashboard/jxy  — JXY 驾驶舱(管理者视角)
  // 严格分工修(2026-07-01):任务系统 + JXY 驾驶舱是租户运营,platform_operator 不看。
  '/tasks/my-todos':      ['tenant_admin', 'manager', 'staff'],
  '/tasks':               ['tenant_admin', 'manager'],
  '/dashboard/jxy':       ['tenant_admin', 'manager'],
  // R3-Sprint8:删除 4 个孤儿 MENU 声明(R3-Sprint7b case-14 暴露 — MENU_VISIBILITY
  // 声明但 routes.tsx / Sidebar.tsx 无 entry)。已登 docs/BACKLOG.md R4 重新加:
  //   /staff             — 用户管理(R4 战略)
  //   /portal            — 企业主子门户入口(R4 战略)
  //   /mentor-workbench  — 导师工作台(R4 战略)
  //   /investment        — 投资方台账(R4 战略)
  // 删除原因:留着会触发反漂移 case-14 报错,且让 canSee() 返 true 但 Sidebar 不渲染,
  // 给用户造成"菜单存在但找不到"的困惑。R4 真做时同步加 route + Sidebar entry + MENU。
};

export function canSee(menuKey: string, role: Role | null | undefined): boolean {
  // Sec Reviewer med#3：白名单原则 — 未声明 menuKey 默认拒，强制新增页面显式入 MENU_VISIBILITY。
  // 注意：这是 UX 防线，后端 require_permission 才是真守卫（前端可被绕过）。
  if (!role) return false;
  const allowed = MENU_VISIBILITY[menuKey];
  if (!allowed) return false; // 未声明 → 默认不可见（白名单）
  return allowed.includes(role);
}
